/**
 * Session-scoped, pipeline-scoped store of dismissed pipeline suggestions
 * (SE-5490, contract from SE-5523).
 *
 * ## Why this is a client-only store
 *
 * Dismiss on the Suggested tab means "hide this from ME, for now". It is
 * explicitly **FE-owned with ZERO backend side effects**.
 *
 * It must NEVER route through `RemovedPlayerRecommendation` or any other
 * server-side removal. That mechanism is an ORGANIZATION-WIDE, PERSISTED
 * removal: using it here would permanently delete a recommendation for every
 * other user in the organization, triggered by an affordance that reads as
 * "hide this card for me right now". The blast-radius mismatch is the entire
 * reason this state lives in memory. There is no mutation in this module, and
 * `useDismissedSuggestions` fires none — that is asserted by a unit test that
 * fails if any GraphQL operation is sent on Dismiss.
 *
 * ## Scoping
 *
 * - **Per pipeline** — the map is keyed by `kanbanBoardId`, so dismissing a
 *   candidate in pipeline A leaves it visible in pipeline B. The same player
 *   can be a good idea for one motion and a bad one for another.
 * - **Per candidate** — the inner key is the `PipelineSuggestedPlayer.id`,
 *   which SE-5523 guarantees is stable across nightly snapshots, so a dismissal
 *   survives a snapshot refresh within the session instead of the candidate
 *   popping back.
 * - **Per session** — this is an in-memory `makeVar`. A reload starts clean, so
 *   a dismissal is never a durable decision the user has to undo.
 * - **Per user** — in-memory alone is not enough, because signing out does not
 *   reload the web app. {@link resetDismissedSuggestions} is called from the
 *   sign-out reset path so one user's dismissals cannot leak into the next
 *   session in the same tab.
 * @module features/player-kanban/stores/dismissedSuggestionsStore
 */

import { makeVar } from "@apollo/client";

/** `kanbanBoardId` → the suggestion ids dismissed on that pipeline. */
export type DismissedSuggestionsByBoard = Readonly<
  Record<string, readonly string[]>
>;

/**
 * The dismissal map. Deliberately NOT persisted to AsyncStorage: a dismissal is
 * a session-lifetime "not now", not a durable preference.
 */
export const dismissedSuggestionsVar = makeVar<DismissedSuggestionsByBoard>({});

/**
 * Dismiss one candidate on one pipeline.
 *
 * Idempotent — dismissing twice leaves the state (and its identity) unchanged,
 * so a double-click cannot produce duplicate ids or a needless re-render.
 * @param kanbanBoardId - The pipeline the dismissal is scoped to.
 * @param suggestionId - The candidate's stable `PipelineSuggestedPlayer.id`.
 */
export const dismissSuggestion = (
  kanbanBoardId: string,
  suggestionId: string
): void => {
  const current = dismissedSuggestionsVar();
  const forBoard = current[kanbanBoardId] ?? [];
  if (forBoard.includes(suggestionId)) return;
  dismissedSuggestionsVar({
    ...current,
    [kanbanBoardId]: [...forBoard, suggestionId],
  });
};

/**
 * Read the dismissed ids for one pipeline.
 * @param kanbanBoardId - The pipeline to read.
 * @returns The dismissed suggestion ids, empty when nothing is dismissed.
 */
export const getDismissedSuggestions = (
  kanbanBoardId: string
): readonly string[] => dismissedSuggestionsVar()[kanbanBoardId] ?? [];

/**
 * Drop every dismissal. Called from the sign-out reset path so dismissals are
 * scoped to the signed-in user and not merely to the tab.
 */
export const resetDismissedSuggestions = (): void => {
  dismissedSuggestionsVar({});
};
